import { Bell, Brain, Globe, Shield, Zap, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Brain,
    title: 'AI-Powered Reminders',
    description: 'Our intelligent reminder engine learns from your habits to surface the right reminders at exactly the right time.',
  },
  {
    icon: Globe,
    title: 'Available Worldwide',
    description: 'RemindMeOf serves users across the globe, with reliable delivery of reminder notifications wherever you are.',
  },
  {
    icon: Zap,
    title: 'Instant Notifications',
    description: 'Get reminded via email the moment it matters — a day, a week, or a month before your deadline.',
  },
  {
    icon: Shield,
    title: 'Secure & Private',
    description: 'Your data is encrypted at rest and in transit. We never sell or share your personal information.',
  },
  {
    icon: Users,
    title: 'Built for Everyone',
    description: 'From individuals managing personal goals to teams tracking shared deadlines — RemindMeOf scales with you.',
  },
  {
    icon: Bell,
    title: 'Never Miss a Thing',
    description: 'Appointments, renewals, birthdays, project milestones — if it matters to you, we help you remember it.',
  },
];

export default function About() {
  return (
    <div className="max-w-5xl mx-auto">
      {/* Hero */}
      <section className="text-center py-16">
        <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
          <Bell className="w-4 h-4" />
          About RemindMeOf
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          AI-Driven Reminders for<br className="hidden md:block" /> a Forgetful World
        </h1>
        <p className="text-xl text-gray-500 max-w-2xl mx-auto">
          We believe no important moment should slip through the cracks. RemindMeOf is an intelligent reminder platform
          built to help people and organisations stay on top of what matters most.
        </p>
      </section>

      {/* Mission */}
      <section className="bg-blue-600 text-white rounded-2xl p-10 md:p-14 mb-16 text-center">
        <h2 className="text-3xl font-bold mb-4">Our Mission</h2>
        <p className="text-blue-100 text-lg max-w-3xl mx-auto leading-relaxed">
          To eliminate the stress of forgetting by combining the power of artificial intelligence with
          effortlessly simple reminder management — making timely, reliable notifications accessible to everyone on the planet.
        </p>
      </section>

      {/* Features grid */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Why RemindMeOf?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map(({ icon: Icon, title, description }) => (
            <div key={title} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Company */}
      <section className="bg-gray-50 rounded-2xl p-10 md:p-14 mb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">About VarBiz Ltd</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            RemindMeOf is a flagship product of <span className="font-semibold text-gray-800">VarBiz Ltd</span>, a technology
            company dedicated to building intelligent digital tools that simplify everyday life. Founded with a vision to
            harness the potential of AI for practical, human-centred applications, VarBiz Ltd develops software solutions
            that are trusted by thousands of users worldwide.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            Headquartered in the United Kingdom, VarBiz Ltd operates globally and is committed to delivering high-quality,
            privacy-respecting products that genuinely help people accomplish more — one reminder at a time.
          </p>
          <p className="text-gray-600 leading-relaxed">
            We are a small, passionate team of engineers, designers, and product thinkers who care deeply about the
            tools we build and the people who use them.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="text-center pb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to get started?</h2>
        <p className="text-gray-500 mb-8">Join thousands of users who never miss an important date.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/register" className="btn-primary px-8 py-3 text-base">
            Create Free Account
          </Link>
          <Link to="/contact" className="btn-secondary px-8 py-3 text-base">
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
}
